import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { askFrank, Source } from "@/services/frank";
import { fetchAssociationId } from "@/utils/associationUtils";
import { 
  Message,
  createUserMessage,
  createFrankMessage,
  createFrankErrorMessage,
  handleFrankError,
  formatTime,
  getFrankApiUrl,
  getFrankThreshold
} from "@/utils/frankMessageUtils";
import { useLanguage } from "@/contexts/LanguageContext";

// Change regular export to type export
export { formatTime };
export type { Message };

export const useFrank = (memberContext?: { id: string; organization_id: string } | null) => {
  const { language } = useLanguage();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [associationId, setAssociationId] = useState<string>("");
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [apiStatus, setApiStatus] = useState<'available' | 'unavailable' | 'unknown'>('unknown');
  const [isInitializing, setIsInitializing] = useState(true);
  const [initializationRetries, setInitializationRetries] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [currentApiUrl, setCurrentApiUrl] = useState<string>("");
  const [currentThreshold, setCurrentThreshold] = useState<number>(0.4);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();

  // Enhanced initialization with retry logic - now depends on member context
  useEffect(() => {
    // Only initialize Frank if we have member context
    if (memberContext && memberContext.id && memberContext.organization_id) {
      console.log('Frank: Member context available, initializing...', memberContext);
      initializeFrankWithRetry();
    } else if (memberContext === null) {
      // Member context is explicitly null (not loading), set error state
      console.log('Frank: No member context available');
      setConnectionError("Unable to access your organization information. Please refresh the page or contact support.");
      setApiStatus('unavailable');
      setIsInitializing(false);
    }
    // If memberContext is undefined, we're still loading, so keep initializing state
    
    // Listen for localStorage changes to update API URL and threshold
    const handleStorageChange = () => {
      const apiUrl = getFrankApiUrl();
      const threshold = getFrankThreshold();
      setCurrentApiUrl(apiUrl);
      setCurrentThreshold(threshold);
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Set initial values
    setCurrentApiUrl(getFrankApiUrl());
    setCurrentThreshold(getFrankThreshold());
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [memberContext]); // Depend on memberContext instead of empty deps

  // Scroll to bottom only when messages change and there are messages
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  /**
   * Initialize Frank with retry logic
   */
  const initializeFrankWithRetry = async () => {
    setIsInitializing(true);
    
    const id = await fetchAssociationId();
    if (id) {
      console.log('Frank: Successfully initialized with association ID:', id);
      setAssociationId(id);
      setApiStatus('available');
      setConnectionError(null);
      setInitializationRetries(0);
      setIsInitializing(false);
      return;
    }
    
    // Retry logic with exponential backoff
    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, initializationRetries), 10000); // Max 10s
    
    if (initializationRetries < maxRetries) {
      console.warn(`Frank: Initialization failed, retrying in ${retryDelay}ms (attempt ${initializationRetries + 1}/${maxRetries})`);
      setInitializationRetries(prev => prev + 1);
      
      retryTimeoutRef.current = setTimeout(() => {
        initializeFrankWithRetry();
      }, retryDelay);
    } else {
      console.error('Frank: Failed to initialize after maximum retries');
      setConnectionError("Could not retrieve your association information. Please try refreshing the page or contact support.");
      setApiStatus('unavailable');
      setIsInitializing(false);
      toast.error("Frank AI is temporarily unavailable. Please try again later.");
    }
  };

  /**
   * Manual retry function for user-triggered retries
   */
  const retryInitialization = () => {
    setInitializationRetries(0);
    setConnectionError(null);
    initializeFrankWithRetry();
  };

  /**
   * Scroll chat window to the bottom
   */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  /**
   * Handle sending a message to Frank
   */
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;
    
    // Check if we have an association ID
    if (!associationId) {
      toast.error("Missing association information. Please try refreshing the page.");
      return;
    }
    
    // Add user message to chat
    const userMessage = createUserMessage(newMessage);
    setMessages(prev => [...prev, userMessage]);
    setNewMessage("");
    setIsLoading(true);
    setConnectionError(null);
    
    try {
      // Send message to Frank API with language and context
      const frankResponse = await askFrank(userMessage.text, associationId, language, {
        // Note: We don't have direct access to organizationId and memberId in this hook
        // but the logger will still capture what it can from the association ID
      });
      console.log('Frank response:', JSON.stringify(frankResponse, null, 2));
      
      // Improved logging about sources
      if (frankResponse.sources && Array.isArray(frankResponse.sources)) {
        console.log(`Frank response contains ${frankResponse.sources.length} sources`);
      } else {
        console.log('Frank response contains no sources array');
      }
      
      // Add Frank's response to chat
      const frankMessage = createFrankMessage(
        frankResponse.answer, 
        frankResponse.source, 
        frankResponse.sources
      );
      
      console.log('Creating Frank message with sources:', JSON.stringify(frankResponse.sources, null, 2));
      setMessages(prev => [...prev, frankMessage]);
      setApiStatus('available');
    } catch (error: any) {
      const { shouldUpdateApiStatus, errorMessage, displayError } = handleFrankError(error);
      
      if (shouldUpdateApiStatus) {
        setConnectionError(errorMessage || "The Frank AI service is currently unavailable. Please try again later or contact support.");
        setApiStatus('unavailable');
      } else if (error.message && error.message.includes("Server error (500)")) {
        // Handle 500 error specifically
        setConnectionError("The Frank AI service encountered an internal server error. This might be a temporary issue. Please try again later.");
        toast.error("Frank AI service error. Please try again later.");
      }
      
      // Add error message to chat
      setMessages(prev => [...prev, createFrankErrorMessage(displayError)]);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    messages,
    newMessage,
    setNewMessage,
    isLoading,
    isInitializing,
    messagesEndRef,
    handleSendMessage,
    formatTime,
    connectionError,
    apiStatus,
    currentApiUrl,
    currentThreshold,
    retryInitialization
  };
};
